// src/components/workflow/WorkflowValidationActions.tsx

import React, { useState } from 'react';
import { CheckCircle, XCircle, RotateCcw, Shield, Building2, User } from 'lucide-react';
import { Button, Modal, Textarea, Badge } from '../ui';

interface ValidationStep {
  step_order: number;
  step_label: string | null;
  can_resolve_here: boolean;
  validator: { id: number; name: string } | null;
  status: string;
}

interface Props {
  currentStep: ValidationStep | null;
  canApprove: boolean;
  canReject: boolean;
  canResolve: boolean;
  processing: boolean;
  laporan?: {
    kode?: string;
    judul?: string;
    tingkat_risiko?: string;
    unit?: { name: string } | null;
    user?: { name: string } | null;
  };
  onApprove: (feedback?: string) => Promise<boolean>;
  onReject: (feedback: string) => Promise<boolean>;
  onResolve: (feedback: string) => Promise<boolean>;
}

type ActionType = 'approve' | 'reject' | 'resolve' | null;

const WorkflowValidationActions: React.FC<Props> = ({
  currentStep,
  canApprove,
  canReject,
  canResolve,
  processing,
  laporan,
  onApprove,
  onReject,
  onResolve,
}) => {
  const [selectedAction, setSelectedAction] = useState<ActionType>(null);
  const [feedback, setFeedback] = useState('');
  const [localProcessing, setLocalProcessing] = useState(false);

  if (!currentStep || currentStep.status !== 'pending') {
    return null;
  }

  const openModal = (action: ActionType) => {
    setSelectedAction(action);
    setFeedback('');
  };

  const closeModal = () => {
    setSelectedAction(null);
    setFeedback('');
  };

  const handleConfirm = async () => {
    if (!selectedAction) return;

    if ((selectedAction === 'reject' || selectedAction === 'resolve') && !feedback.trim()) {
      return;
    }

    setLocalProcessing(true);
    let success = false;

    try {
      switch (selectedAction) {
        case 'approve':
          success = await onApprove(feedback || undefined);
          break;
        case 'reject':
          success = await onReject(feedback);
          break;
        case 'resolve':
          success = await onResolve(feedback);
          break;
      }

      if (success) {
        closeModal();
      }
    } finally {
      setLocalProcessing(false);
    }
  };

  const isProcessing = processing || localProcessing;

  const modalConfig = {
    approve: {
      title: `Setujui: ${currentStep.step_label}`,
      variant: 'success' as const,
      icon: <CheckCircle className="w-5 h-5" />,
      message: 'Laporan akan disetujui dan diteruskan ke tahap berikutnya.',
      requireFeedback: false,
      buttonLabel: 'Setujui',
    },
    reject: {
      title: 'Tolak Laporan',
      variant: 'danger' as const,
      icon: <XCircle className="w-5 h-5" />,
      message: 'Laporan akan ditolak dan dikembalikan ke pelapor untuk revisi.',
      requireFeedback: true,
      buttonLabel: 'Tolak Laporan',
    },
    resolve: {
      title: 'Selesaikan di Level Ini',
      variant: 'primary' as const,
      icon: <Shield className="w-5 h-5" />,
      message: 'Masalah akan diselesaikan di level Anda tanpa naik ke tingkat lebih tinggi. Rektor & Sekretaris akan menerima laporan.',
      requireFeedback: true,
      buttonLabel: 'Selesaikan di Level Ini',
    },
  };

  const config = selectedAction ? modalConfig[selectedAction] : null;

  return (
    <>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {canApprove && (
          <Button
            variant="success"
            size="sm"
            icon={<CheckCircle className="w-4 h-4" />}
            onClick={() => openModal('approve')}
            disabled={isProcessing}
          >
            Setujui
          </Button>
        )}

        {canResolve && (
          <Button
            variant="primary"
            size="sm"
            icon={<Shield className="w-4 h-4" />}
            onClick={() => openModal('resolve')}
            disabled={isProcessing}
          >
            Selesaikan di Level Ini
          </Button>
        )}

        {canReject && (
          <Button
            variant="outline"
            size="sm"
            icon={<RotateCcw className="w-4 h-4" />}
            onClick={() => openModal('reject')}
            disabled={isProcessing}
          >
            Minta Revisi
          </Button>
        )}
      </div>

      {/* Current Step Info */}
      <div className="mt-2 text-xs text-neutral-text-secondary flex items-center gap-2">
        <span className="px-2 py-0.5 bg-teal-main/10 text-teal-main rounded-full font-semibold">
          Step {currentStep.step_order}
        </span>
        <span>{currentStep.step_label}</span>
        {currentStep.validator && (
          <span className="text-neutral-text-secondary">
            → {currentStep.validator.name}
          </span>
        )}
      </div>

      {/* Confirmation Modal */}
      {config && (
        <Modal
          isOpen={!!selectedAction}
          onClose={closeModal}
          title={config.title}
          size="md"
          footer={
            <>
              <Button variant="outline" onClick={closeModal} disabled={isProcessing}>
                Batal
              </Button>
              <Button
                variant={config.variant}
                onClick={handleConfirm}
                loading={isProcessing}
                disabled={config.requireFeedback && !feedback.trim()}
              >
                {config.buttonLabel}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {/* Info Banner */}
            <div
              className={`p-3 rounded-xl border text-sm font-medium flex items-start gap-3 ${
                selectedAction === 'approve'
                  ? 'bg-success/5 border-success/20 text-success'
                  : selectedAction === 'reject'
                    ? 'bg-danger/5 border-danger/20 text-danger'
                    : 'bg-info/5 border-info/20 text-info'
              }`}
            >
              {config.icon}
              <span>{config.message}</span>
            </div>

            {/* Report Card */}
            {laporan && (
              <div className="p-4 bg-neutral-bg rounded-xl space-y-2">
                <p className="text-xs font-mono text-teal-main">{laporan.kode || '-'}</p>
                <p className="text-sm font-bold text-neutral-text-primary">{laporan.judul || '-'}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {laporan.tingkat_risiko && (
                    <Badge variant="risk" risk={laporan.tingkat_risiko as any} />
                  )}
                  {laporan.unit?.name && (
                    <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {laporan.unit.name}
                    </span>
                  )}
                  {laporan.user?.name && (
                    <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                      <User className="w-3 h-3" /> {laporan.user.name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Step Info */}
            <div className="p-3 bg-teal-main/5 rounded-xl">
              <p className="text-xs font-semibold text-teal-main">
                Step {currentStep.step_order}: {currentStep.step_label}
              </p>
              {currentStep.can_resolve_here && selectedAction === 'approve' && (
                <p className="text-[10px] text-teal-700 mt-1">
                  💡 Anda juga bisa memilih "Selesaikan di Level Ini" jika masalah ini bisa ditangani tanpa naik ke level atas.
                </p>
              )}
            </div>

            {/* Feedback */}
            <Textarea
              label={config.requireFeedback ? 'Catatan (wajib)' : 'Catatan (opsional)'}
              placeholder={
                selectedAction === 'reject'
                  ? 'Jelaskan alasan penolakan dan apa yang perlu diperbaiki...'
                  : selectedAction === 'resolve'
                    ? 'Jelaskan tindak lanjut yang akan dilakukan untuk menyelesaikan masalah ini...'
                    : 'Tambahkan catatan jika perlu...'
              }
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              required={config.requireFeedback}
              rows={4}
            />

            {config.requireFeedback && !feedback.trim() && (
              <p className="text-xs text-danger flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                Catatan wajib diisi untuk melanjutkan
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default WorkflowValidationActions;